import React, { useState, useEffect } from 'react';
import { packagesService } from '../services/packagesService';
import { useApp } from '../context/AppContext';
import { 
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  CalendarIcon,
  ClockIcon,
  TicketIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const DateSlotsSidebar = ({ isOpen, onClose, packageData, onSlotAdded }) => {
  const { showToast } = useApp();
  const [dateSlots, setDateSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSlot, setNewSlot] = useState({
    date: '',
    capacityPerday: ''
  });

  useEffect(() => {
    if (isOpen && packageData) {
      setDateSlots(packageData.dateSlots || []);
    }
  }, [isOpen, packageData]);


  const handleAddSlot = async () => {
    if (!newSlot.date) {
      setError('Please select a date');
      return;
    }

    if (!newSlot.capacityPerday || isNaN(newSlot.capacityPerday) || parseInt(newSlot.capacityPerday) <= 0) {
      setError('Please enter a valid capacity per day (number greater than 0)');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get existing date slots from the package
      const existingSlots = packageData.dateSlots || [];
      
      // Create the new date slot with capacity in the required format
      const dateSlotsDetails = {
        date: newSlot.date,
        capacityPerday: parseInt(newSlot.capacityPerday)
      };
      
      // Add the new slot to the existing slots array
      const updatedSlots = [...existingSlots, dateSlotsDetails];
      
      const response = await packagesService.updateDateSlots(packageData._id, {
        dateSlots: updatedSlots
      });

      if (response.success) {
        showToast('success', response.message || 'Date slot added successfully');
        // Reset form
        setNewSlot({
          date: '',
          capacityPerday: ''
        });
        setShowAddForm(false);
        
        // Close sidebar and refresh package list
        onSlotAdded && onSlotAdded();
        handleClose();
      } else {
        setError(response.message || 'Failed to add date slot');
      }
    } catch (error) {
      console.error('Error adding date slot:', error);
      setError('Failed to add date slot. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteSlot = async (dateToDelete) => {
    if (!window.confirm('Are you sure you want to delete this date slot?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use the delete API with the specific date to delete
      const response = await packagesService.deleteDateSlot(packageData._id, [dateToDelete]);

      if (response.success) {
        showToast('success', response.message || 'Date slot deleted successfully');
        // Close sidebar and refresh package list
        onSlotAdded && onSlotAdded();
        handleClose();
      } else {
        setError(response.message || 'Failed to delete date slot');
      }
    } catch (error) {
      console.error('Error deleting date slot:', error);
      setError('Failed to delete date slot. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDateSlots([]);
    setNewSlot({
      date: '',
      capacityPerday: ''
    });
    setShowAddForm(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />
      
      {/* Sidebar */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center">
              <TicketIcon className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Date Slots</h2>
                <p className="text-sm text-gray-500">{packageData?.title}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Add Date Form */}
            {showAddForm && (
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Add Date Slot
                </h3>
                
                <div className="space-y-4">
                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={newSlot.date}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        const existingSlots = packageData.dateSlots || [];
                        // Check if date already exists (handle both object and string formats)
                        const isDuplicate = existingSlots.some(slot => {
                          const slotDate = typeof slot === 'string' ? slot : slot.date;
                          return slotDate === selectedDate;
                        });
                        
                        if (isDuplicate) {
                          setError('This date is already added. Please select a different date.');
                          return;
                        } else {
                          setError(null);
                          setNewSlot(prev => ({ ...prev, date: selectedDate }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                    {dateSlots.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Existing dates (cannot be selected):</p>
                        <div className="flex flex-wrap gap-1">
                          {dateSlots.map((slot, index) => {
                            const slotDate = typeof slot === 'string' ? slot : slot.date;
                            return (
                              <span 
                                key={index} 
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                              >
                                {new Date(slotDate).toLocaleDateString()}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Capacity Per Day */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Capacity Per Day <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={newSlot.capacityPerday}
                      onChange={(e) => setNewSlot(prev => ({ ...prev, capacityPerday: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter capacity per day"
                      min="1"
                      required
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewSlot({
                          date: '',
                          capacityPerday: ''
                        });
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddSlot}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Add Date'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Date Slots List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Existing Date Slots</h3>
                {!showAddForm && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Date Slot
                  </button>
                )}
              </div>

              {dateSlots.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No date slots</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    This package doesn't have any date slots yet.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => setShowAddForm(true)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add First Date Slot
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {dateSlots.map((slot, index) => {
                    // Handle both string date format and object format
                    const slotDate = typeof slot === 'string' ? slot : slot.date;
                    const slotCapacity = typeof slot === 'object' && slot.capacityPerday ? slot.capacityPerday : 'N/A';
                    
                    return (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {new Date(slotDate).toLocaleDateString()}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                Capacity Per Day: {slotCapacity}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDeleteSlot(slotDate)}
                              className="text-gray-400 hover:text-red-600"
                              title="Delete date slot"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateSlotsSidebar;
